import React, { useState, useEffect } from "react";
import { Container, Box, Button, Typography, Paper, Tooltip, Fab } from "@material-ui/core";
import { Rating } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import StorefrontIcon from "@material-ui/icons/Storefront";
import NatureIcon from "@material-ui/icons/Nature";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ScheduleIcon from "@material-ui/icons/Schedule";
import PhoneIcon from "@material-ui/icons/Phone";
import PlaceIcon from "@material-ui/icons/Place";
import PaymentIcon from "@material-ui/icons/Payment";
import FavoriteIcon from "@material-ui/icons/Favorite";
import ReceiptIcon from "@material-ui/icons/Receipt";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { CUSTOMER_ORDER } from "../../constants/routes";
import { withFirebase } from "../Firebase";
import { isDeliveryAreaOpen } from "../../utilities/businessHoursValidation";

const BRAND = {
  navy: "#0d47a1",
  navyDeep: "#082c61",
  navySoft: "#1565c0",
  amber: "#F59E0B",
  amberDeep: "#D97706",
  cream: "#FFF8EC",
  ink: "#1F2937",
  mute: "#5A6473",
  whatsapp: "#25D366",
  whatsappHover: "#1DA851",
  line: "#E6E1D6",
};

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#ffffff",
    overflow: "hidden",
    color: BRAND.ink,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  topBar: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    background: "rgba(255,255,255,0.92)",
    backdropFilter: "saturate(180%) blur(8px)",
    WebkitBackdropFilter: "saturate(180%) blur(8px)",
    borderBottom: `1px solid ${BRAND.line}`,
  },
  topBarInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.25, 2),
  },
  topBarBrand: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  topBarLogo: {
    width: 36,
    height: 36,
    objectFit: "contain",
  },
  topBarName: {
    fontSize: "1.05rem",
    fontWeight: 700,
    color: BRAND.navy,
    letterSpacing: "-0.01em",
  },
  topBarActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  topBarPhone: {
    color: BRAND.navy,
    fontWeight: 600,
    fontSize: "0.95rem",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  topBarChip: {
    background: BRAND.whatsapp,
    color: "#fff",
    padding: theme.spacing(0.75, 1.75),
    borderRadius: 999,
    fontSize: "0.85rem",
    fontWeight: 700,
    textTransform: "none",
    boxShadow: "none",
    "&:hover": { background: BRAND.whatsappHover, boxShadow: "none" },
  },

  hero: {
    minHeight: "92vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundImage: "url('/mercearianena.png')",
    backgroundSize: "cover",
    backgroundPosition: "center bottom",
    backgroundAttachment: "fixed",
    backgroundRepeat: "no-repeat",
    position: "relative",
    overflow: "hidden",
    color: "white",
    padding: theme.spacing(6, 2),
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      background:
        "linear-gradient(180deg, rgba(8,44,97,0.85) 0%, rgba(13,71,161,0.55) 45%, rgba(8,44,97,0.92) 100%)",
      zIndex: 1,
    },
  },
  heroContent: {
    position: "relative",
    zIndex: 2,
    maxWidth: 760,
    textAlign: "center",
  },
  heroEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.28)",
    color: "#fff",
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 999,
    fontSize: "0.8rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2.5),
  },
  heroTitle: {
    fontSize: "3.75rem",
    fontWeight: 800,
    marginBottom: theme.spacing(2),
    lineHeight: 1.05,
    letterSpacing: "-0.02em",
    [theme.breakpoints.down("sm")]: { fontSize: "2.4rem" },
  },
  heroAccent: {
    color: "#FFD78A",
    display: "inline",
  },
  heroSubtitle: {
    fontSize: "1.2rem",
    opacity: 0.95,
    marginBottom: theme.spacing(4),
    lineHeight: 1.6,
    fontWeight: 400,
    maxWidth: 620,
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: { fontSize: "1.05rem" },
  },
  ctaRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: theme.spacing(4),
  },
  ctaPrimary: {
    background: BRAND.amber,
    color: "#1a1a1a",
    padding: theme.spacing(1.6, 3.5),
    fontSize: "1.05rem",
    fontWeight: 700,
    borderRadius: 999,
    textTransform: "none",
    boxShadow: "0 10px 28px rgba(245,158,11,0.45)",
    transition: "all 0.25s ease",
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 260,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 14px 36px rgba(245,158,11,0.55)",
      background: BRAND.amberDeep,
      color: "#fff",
    },
    [theme.breakpoints.down("sm")]: { width: "100%", maxWidth: 360 },
  },
  ctaWhatsApp: {
    background: BRAND.whatsapp,
    color: "#fff",
    padding: theme.spacing(1.6, 3.5),
    fontSize: "1.05rem",
    fontWeight: 700,
    borderRadius: 999,
    textTransform: "none",
    boxShadow: "0 10px 28px rgba(37,211,102,0.4)",
    transition: "all 0.25s ease",
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 260,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 14px 36px rgba(37,211,102,0.55)",
      background: BRAND.whatsappHover,
    },
    [theme.breakpoints.down("sm")]: { width: "100%", maxWidth: 360 },
  },
  ctaButtonDisabled: {
    background: "rgba(255,255,255,0.45)",
    color: "rgba(0,0,0,0.45)",
    cursor: "not-allowed",
    "&:hover": {
      transform: "none",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
      background: "rgba(255,255,255,0.45)",
    },
  },
  closedMessage: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.25, 2.5),
    background: "rgba(255, 193, 7, 0.18)",
    border: "1px solid rgba(255,193,7,0.45)",
    borderRadius: 999,
    color: "#FFE7A8",
    fontSize: "0.95rem",
    fontWeight: 500,
  },

  trustStrip: {
    background: BRAND.cream,
    borderBottom: `1px solid ${BRAND.line}`,
    padding: theme.spacing(2.5, 2),
  },
  trustGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyItems: "center",
  },
  trustItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: BRAND.navyDeep,
    fontWeight: 600,
    fontSize: "0.95rem",
  },
  trustItemIcon: {
    color: BRAND.amberDeep,
    fontSize: "1.4rem",
  },

  section: {
    padding: theme.spacing(10, 2),
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(6, 2) },
  },
  sectionEyebrow: {
    display: "block",
    color: BRAND.amberDeep,
    fontSize: "0.85rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    textAlign: "center",
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: "2.5rem",
    fontWeight: 800,
    color: BRAND.navy,
    marginBottom: theme.spacing(1),
    textAlign: "center",
    letterSpacing: "-0.02em",
    [theme.breakpoints.down("sm")]: { fontSize: "1.85rem" },
  },
  sectionLead: {
    fontSize: "1.05rem",
    color: BRAND.mute,
    textAlign: "center",
    maxWidth: 620,
    margin: "0 auto",
    marginBottom: theme.spacing(5),
    lineHeight: 1.6,
  },

  stepsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: theme.spacing(3),
  },
  stepCard: {
    background: "#fff",
    border: `1px solid ${BRAND.line}`,
    borderRadius: 18,
    padding: theme.spacing(3.5, 3),
    position: "relative",
    transition: "all 0.25s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 18px 40px rgba(13,71,161,0.10)",
      borderColor: BRAND.amber,
    },
  },
  stepNumber: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.navySoft})`,
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: "1.05rem",
    marginBottom: theme.spacing(1.75),
  },
  stepTitle: {
    fontSize: "1.15rem",
    fontWeight: 700,
    color: BRAND.navyDeep,
    marginBottom: theme.spacing(0.75),
  },
  stepText: {
    fontSize: "0.97rem",
    color: BRAND.mute,
    lineHeight: 1.6,
  },

  featuresGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  featureCard: {
    padding: theme.spacing(4, 3),
    borderRadius: 18,
    background: "#fff",
    border: `1px solid ${BRAND.line}`,
    transition: "all 0.25s ease",
    textAlign: "left",
    cursor: "default",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-6px)",
      boxShadow: "0 18px 44px rgba(13,71,161,0.12)",
      borderColor: BRAND.navySoft,
    },
  },
  featureIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: BRAND.cream,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
  featureIcon: {
    fontSize: "1.9rem",
    color: BRAND.amberDeep,
  },
  featureTitle: {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: BRAND.navyDeep,
    marginBottom: theme.spacing(0.75),
  },
  featureText: {
    fontSize: "0.97rem",
    color: BRAND.mute,
    lineHeight: 1.6,
  },

  deliveryInfo: {
    background: `linear-gradient(135deg, ${BRAND.cream} 0%, #FFE9C2 100%)`,
    border: `1px solid #F2C879`,
    borderRadius: 18,
    padding: theme.spacing(3.5, 3),
    marginTop: theme.spacing(4),
    textAlign: "center",
  },
  deliveryTitle: {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "#8C4A03",
    marginBottom: theme.spacing(0.75),
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    justifyContent: "center",
  },
  deliveryText: {
    fontSize: "1rem",
    color: "#6B3502",
    lineHeight: 1.6,
    fontWeight: 500,
  },

  statsSection: {
    background: BRAND.navyDeep,
    color: "#fff",
    padding: theme.spacing(7, 2),
    position: "relative",
    overflow: "hidden",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing(3),
    textAlign: "center",
    position: "relative",
    zIndex: 2,
  },
  statBox: {
    padding: theme.spacing(2),
    borderRight: "1px solid rgba(255,255,255,0.12)",
    "&:last-child": { borderRight: "none" },
    [theme.breakpoints.down("sm")]: { borderRight: "none" },
  },
  statNumber: {
    fontSize: "2.75rem",
    fontWeight: 800,
    color: "#FFD78A",
    letterSpacing: "-0.02em",
  },
  statLabel: {
    fontSize: "0.95rem",
    color: "rgba(255,255,255,0.78)",
    marginTop: theme.spacing(0.5),
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 600,
  },

  footer: {
    background: "#06224a",
    color: "white",
    padding: theme.spacing(6, 2, 4),
    borderTop: `1px solid ${BRAND.navySoft}`,
  },
  footerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: theme.spacing(4),
    maxWidth: 980,
    margin: "0 auto",
    paddingBottom: theme.spacing(3),
    borderBottom: "1px solid rgba(255,255,255,0.10)",
  },
  footerCol: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
  },
  footerHeading: {
    fontSize: "0.8rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#FFD78A",
    marginBottom: theme.spacing(1),
  },
  footerLine: {
    fontSize: "0.97rem",
    color: "rgba(255,255,255,0.92)",
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  footerLink: {
    color: "rgba(255,255,255,0.92)",
    textDecoration: "none",
    "&:hover": { color: "#FFD78A" },
  },
  footerCopy: {
    textAlign: "center",
    paddingTop: theme.spacing(3),
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.6)",
  },

  fab: {
    position: "fixed",
    right: theme.spacing(2.5),
    bottom: theme.spacing(2.5),
    background: BRAND.whatsapp,
    color: "#fff",
    boxShadow: "0 10px 28px rgba(37,211,102,0.5)",
    zIndex: 30,
    "&:hover": { background: BRAND.whatsappHover },
  },
}));

function LandingPage({ firebase }) {
  const classes = useStyles();
  const [isDeliveryOpen, setIsDeliveryOpen] = useState(true);
  const [closedMessage, setClosedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enableOnlineOrder, setEnableOnlineOrder] = useState(false);

  useEffect(() => {
    checkDeliveryAvailability();
    fetchEnableOnlineOrder();
    const interval = setInterval(checkDeliveryAvailability, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEnableOnlineOrder = async () => {
    try {
      const organizationId = process.env.REACT_APP_ORGANIZATION_ID;
      if (!organizationId || !firebase) {
        return;
      }

      const snapshot = await firebase
        .db
        .ref(`organizations/${organizationId}/about/enableOnlineOrder`)
        .once('value');

      setEnableOnlineOrder(!!snapshot.val());
    } catch (error) {
      console.error('Error fetching enableOnlineOrder:', error);
      setEnableOnlineOrder(false);
    }
  };

  const checkDeliveryAvailability = async () => {
    try {
      const organizationId = process.env.REACT_APP_ORGANIZATION_ID;
      if (!organizationId || !firebase) {
        setLoading(false);
        return;
      }

      const snapshot = await firebase
        .deliveryAreas(organizationId)
        .orderByChild("active")
        .equalTo(true)
        .once("value");

      const areasData = snapshot.val();

      if (!areasData) {
        setIsDeliveryOpen(false);
        setClosedMessage(null);
        setLoading(false);
        return;
      }

      let anyAreaOpen = false;
      let nextOpenMessage = null;

      for (const areaId in areasData) {
        const area = areasData[areaId];

        if (!area.businessHours || !area.businessHours.schedule) {
          anyAreaOpen = true;
          break;
        }

        const status = isDeliveryAreaOpen(area.businessHours);
        if (status.isOpen) {
          anyAreaOpen = true;
          break;
        } else if (!nextOpenMessage && status.message) {
          nextOpenMessage = status.message;
        }
      }

      setIsDeliveryOpen(anyAreaOpen);
      setClosedMessage(nextOpenMessage || "Fechado no momento");
      setLoading(false);
    } catch (error) {
      console.error("Error checking delivery availability:", error);
      setIsDeliveryOpen(true);
      setLoading(false);
    }
  };

  const handleWhatsAppClick = (source) => {
    if (firebase && firebase.logEvent) {
      firebase.logEvent("whatsapp_click", { source });
    }
    const phoneNumber = "5585987387795";
    const message = "Oi, gostaria de fazer um pedido.";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <Box className={classes.root}>
      {/* Sticky top bar - quick access without competing with hero */}
      <Box className={classes.topBar}>
        <Container maxWidth="lg" className={classes.topBarInner}>
          <Box className={classes.topBarBrand}>
            <Box component="img" src="/logo.png" alt="Mercadinho Nena" className={classes.topBarLogo} />
            <Typography className={classes.topBarName}>Mercadinho Nena</Typography>
          </Box>
          <Box className={classes.topBarActions}>
            <a href="tel:+558532143548" className={classes.topBarPhone}>
              <PhoneIcon style={{ fontSize: "1rem" }} />
              (85) 3214-3548
            </a>
            <Button
              className={classes.topBarChip}
              onClick={() => handleWhatsAppClick("topbar")}
              startIcon={<WhatsAppIcon />}
            >
              WhatsApp
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box className={classes.hero}>
        <Container maxWidth="md" className={classes.heroContent}>
          <Box
            component="img"
            src="/logo.png"
            alt="Mercadinho Nena"
            style={{
              display: "block",
              maxWidth: 220,
              height: "auto",
              margin: "0 auto 24px",
              zIndex: 2,
              position: "relative",
              filter: "drop-shadow(0 12px 30px rgba(0,0,0,0.35))",
            }}
          />
          <Typography className={classes.heroEyebrow}>
            <FavoriteIcon style={{ fontSize: "0.95rem", color: "#FFD78A" }} />
            Seu mercadinho de bairro, agora online
          </Typography>
          <Typography className={classes.heroTitle}>
            Frescor, confiança e <span className={classes.heroAccent}>entrega rápida</span>.
          </Typography>
          <Typography className={classes.heroSubtitle}>
            Água, produtos naturais e o essencial do dia a dia entregues com carinho na sua porta.
            Peça pelo WhatsApp ou faça seu pedido online em poucos cliques.
          </Typography>

          <Box className={classes.ctaRow}>
            {isDeliveryOpen && enableOnlineOrder && (
              <Tooltip title="" arrow placement="top">
                <span>
                  <Button
                    className={classes.ctaPrimary}
                    component="a"
                    href={CUSTOMER_ORDER}
                    startIcon={<StorefrontIcon />}
                  >
                    Fazer Pedido Online
                    <ArrowForwardIcon style={{ fontSize: "1.2rem" }} />
                  </Button>
                </span>
              </Tooltip>
            )}

            <Button
              className={classes.ctaWhatsApp}
              onClick={() => handleWhatsAppClick("hero")}
              startIcon={<WhatsAppIcon />}
            >
              Pedir pelo WhatsApp
              <ArrowForwardIcon style={{ fontSize: "1.2rem" }} />
            </Button>
          </Box>

          {closedMessage && !isDeliveryOpen && !loading && (
            <Box style={{ marginTop: 24 }}>
              <Box className={classes.closedMessage}>
                <ScheduleIcon style={{ fontSize: "1.1rem" }} />
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {closedMessage}
                </Typography>
              </Box>
            </Box>
          )}
        </Container>
      </Box>

      {/* Trust strip - subtle reassurance right after hero */}
      <Box className={classes.trustStrip}>
        <Container maxWidth="lg">
          <Box className={classes.trustGrid}>
            <Box className={classes.trustItem}>
              <PaymentIcon className={classes.trustItemIcon} />
              Pagamento via PIX
            </Box>
            <Box className={classes.trustItem}>
              <LocalShippingIcon className={classes.trustItemIcon} />
              Entrega no mesmo dia
            </Box>
            <Box className={classes.trustItem}>
              <WhatsAppIcon className={classes.trustItemIcon} />
              Atendimento humano
            </Box>
            <Box className={classes.trustItem}>
              <CheckCircleIcon className={classes.trustItemIcon} />
              Produtos selecionados
            </Box>
          </Box>
        </Container>
      </Box>

      {/* How it works - new section to surface online ordering capability */}
      <Box className={classes.section}>
        <Container maxWidth="lg">
          <Typography component="span" className={classes.sectionEyebrow}>
            Simples assim
          </Typography>
          <Typography className={classes.sectionTitle}>Como funciona</Typography>
          <Typography className={classes.sectionLead}>
            Em três passos seu pedido chega quentinho na sua porta.
          </Typography>

          <Box className={classes.stepsGrid}>
            <Paper className={classes.stepCard} elevation={0}>
              <Box className={classes.stepNumber}>1</Box>
              <Typography className={classes.stepTitle}>Escolha seus produtos</Typography>
              <Typography className={classes.stepText}>
                Monte seu pedido online ou converse direto com a gente pelo WhatsApp.
                Você decide o que é mais cômodo.
              </Typography>
            </Paper>

            <Paper className={classes.stepCard} elevation={0}>
              <Box className={classes.stepNumber}>2</Box>
              <Typography className={classes.stepTitle}>Pague com PIX</Typography>
              <Typography className={classes.stepText}>
                Pagamento rápido e seguro pelo Mercado Pago. O QR Code aparece na hora,
                a confirmação é automática.
              </Typography>
            </Paper>

            <Paper className={classes.stepCard} elevation={0}>
              <Box className={classes.stepNumber}>3</Box>
              <Typography className={classes.stepTitle}>Receba em casa</Typography>
              <Typography className={classes.stepText}>
                Entregamos no mesmo dia em Parquelandia, São Gerardo, Monte Castelo
                e arredondezas. Acompanhe pelo link de rastreio.
              </Typography>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box className={classes.section} style={{ background: BRAND.cream, paddingTop: 80, paddingBottom: 80 }}>
        <Container maxWidth="lg">
          <Typography component="span" className={classes.sectionEyebrow}>
            O nosso jeito
          </Typography>
          <Typography className={classes.sectionTitle}>Por que escolher o Mercadinho Nena?</Typography>
          <Typography className={classes.sectionLead}>
            Atendimento próximo, produtos selecionados e a praticidade de receber em casa.
          </Typography>

          <Box className={classes.featuresGrid}>
            <Paper className={classes.featureCard} elevation={0}>
              <Box className={classes.featureIconWrap}>
                <LocalShippingIcon className={classes.featureIcon} />
              </Box>
              <Typography className={classes.featureTitle}>Entrega Rápida</Typography>
              <Typography className={classes.featureText}>
                Entregamos em Parquelandia, São Gerardo, Monte Castelo e arredondezas
                com rapidez e segurança.
              </Typography>
            </Paper>

            <Paper className={classes.featureCard} elevation={0}>
              <Box className={classes.featureIconWrap}>
                <NatureIcon className={classes.featureIcon} />
              </Box>
              <Typography className={classes.featureTitle}>Produtos Naturais</Typography>
              <Typography className={classes.featureText}>
                Selecionados com cuidado para garantir qualidade e frescor em cada entrega.
              </Typography>
            </Paper>

            <Paper className={classes.featureCard} elevation={0}>
              <Box className={classes.featureIconWrap}>
                <StorefrontIcon className={classes.featureIcon} />
              </Box>
              <Typography className={classes.featureTitle}>Melhor Preço</Typography>
              <Typography className={classes.featureText}>
                Oferecemos os melhores preços da região sem comprometer a qualidade.
              </Typography>
            </Paper>
          </Box>

          <Box className={classes.deliveryInfo}>
            <Typography className={classes.deliveryTitle}>
              <PlaceIcon style={{ fontSize: "1.3rem" }} />
              Área de Entrega
            </Typography>
            <Typography className={classes.deliveryText}>
              Entregamos em: Parquelandia, São Gerardo, Monte Castelo e arredondezas
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box className={classes.statsSection}>
        <Container maxWidth="lg">
          <Box className={classes.statsGrid}>
            <Box className={classes.statBox}>
              <Typography className={classes.statNumber}>100+</Typography>
              <Typography className={classes.statLabel}>Clientes Satisfeitos</Typography>
            </Box>
            <Box className={classes.statBox}>
              <Typography className={classes.statNumber}>1h</Typography>
              <Typography className={classes.statLabel}>Entrega em Média</Typography>
            </Box>
            <Box className={classes.statBox}>
              <Box style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
                <Rating value={4} readOnly size="large" style={{ color: "#FFD78A" }} />
              </Box>
              <Typography className={classes.statNumber}>4.0</Typography>
              <Typography className={classes.statLabel}>Avaliação</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box className={classes.footer}>
        <Container maxWidth="lg">
          <Box className={classes.footerGrid}>
            <Box className={classes.footerCol}>
              <Typography className={classes.footerHeading}>Mercadinho Nena</Typography>
              <Typography className={classes.footerLine}>
                Seu mercadinho de bairro, agora também online.
              </Typography>
            </Box>

            <Box className={classes.footerCol}>
              <Typography className={classes.footerHeading}>Contato</Typography>
              <Typography className={classes.footerLine}>
                <PhoneIcon style={{ fontSize: "1rem" }} />
                <a href="tel:+5585987387795" className={classes.footerLink}>(85) 98738-7795</a>
              </Typography>
              <Typography className={classes.footerLine}>
                <PhoneIcon style={{ fontSize: "1rem" }} />
                <a href="tel:+558532143548" className={classes.footerLink}>(85) 3214-3548</a>
              </Typography>
              <Typography className={classes.footerLine}>
                <WhatsAppIcon style={{ fontSize: "1rem" }} />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => handleWhatsAppClick("footer")}
                  onKeyPress={(e) => (e.key === "Enter" ? handleWhatsAppClick("footer") : null)}
                  className={classes.footerLink}
                  style={{ cursor: "pointer" }}
                >
                  Conversar pelo WhatsApp
                </span>
              </Typography>
            </Box>

            <Box className={classes.footerCol}>
              <Typography className={classes.footerHeading}>Horário</Typography>
              <Typography className={classes.footerLine}>
                <ScheduleIcon style={{ fontSize: "1rem" }} />
                Segunda a sábado
              </Typography>
              <Typography className={classes.footerLine}>
                <ScheduleIcon style={{ fontSize: "1rem", opacity: 0 }} />
                8h às 18h
              </Typography>
            </Box>

            <Box className={classes.footerCol}>
              <Typography className={classes.footerHeading}>Pagamento</Typography>
              <Typography className={classes.footerLine}>
                <PaymentIcon style={{ fontSize: "1rem" }} />
                PIX (Mercado Pago)
              </Typography>
              <Typography className={classes.footerLine}>
                <ReceiptIcon style={{ fontSize: "1rem" }} />
                Confirmação automática
              </Typography>
            </Box>
          </Box>

          <Typography className={classes.footerCopy}>
            © 2026 Mercadinho Nena. Todos os direitos reservados.
          </Typography>
        </Container>
      </Box>

      {/* Floating WhatsApp - persistent contact channel */}
      <Tooltip title="Falar pelo WhatsApp" placement="left" arrow>
        <Fab
          className={classes.fab}
          aria-label="Falar pelo WhatsApp"
          onClick={() => handleWhatsAppClick("fab")}
        >
          <WhatsAppIcon />
        </Fab>
      </Tooltip>
    </Box>
  );
}

export default withFirebase(LandingPage);
